// KPI-28 — gözden geçirme tablosu: arama + süzgeç çipleri + sayfalama; satır içi düzenleme
// (karar / kod / etiket / birim / üst kategori / türetilmiş), map_existing'de katalogdaki hal +
// "güncelle" kutusu, sorun rozetleri. Durum dışarıda (modal), burada yalnız görüntü + patch.
// Kırılım (spec §7.3): grup/üye satırında soluk "kırılım" rozeti; üst sütununda kategori seçici
// yerine üst yolu ("Toplam çalışan › Cinsiyete göre") — üstü dosyanın ağacı belirler.

import { useMemo, useState } from "react";
import { isUnitTemplate } from "@mahir/shared";
import type { CatalogImportAction, CatalogImportRowDto } from "../../../api/kpi-catalog-import";
import {
  KPI_CATALOG_IMPORT_PAGE_SIZE,
  breakdownParentPath,
  categoryOptions,
  isBreakdownImportRow,
  confidenceBand,
  filterRows,
  issueLabel,
  paginate,
  type CatalogImportFilter,
  type RowIssue,
  type RowPatch,
} from "../../../lib/kpi-catalog-import";
import { KPI_CATALOG_IMPORT_COPY } from "../../../lib/kpi-catalog-import-copy";
import {
  bandRowBg,
  btnGhost,
  btnSmall,
  inputStyle,
  monoStyle,
  pillStyle,
  tdStyle,
  thStyle,
} from "../kpi-form-styles";

export interface CatalogImportReviewTableProps {
  rows: readonly CatalogImportRowDto[];
  issuesByRow: ReadonlyMap<string, readonly RowIssue[]>;
  existingCategories: readonly { code: string; label: string }[];
  onPatch: (rowId: string, patch: RowPatch) => void;
  disabled?: boolean;
}

const ACTIONS: readonly CatalogImportAction[] = ["create", "map_existing", "skip"];
const FILTERS: readonly { key: CatalogImportFilter; label: string }[] = [
  { key: "all", label: KPI_CATALOG_IMPORT_COPY.review.filterAll },
  { key: "create", label: KPI_CATALOG_IMPORT_COPY.review.filterCreate },
  { key: "map_existing", label: KPI_CATALOG_IMPORT_COPY.review.filterMapExisting },
  { key: "skip", label: KPI_CATALOG_IMPORT_COPY.review.filterSkip },
  { key: "category", label: KPI_CATALOG_IMPORT_COPY.review.filterCategory },
  { key: "issues", label: KPI_CATALOG_IMPORT_COPY.review.filterIssues },
];

const cellInput: React.CSSProperties = { ...inputStyle, minHeight: 32, padding: "4px 8px" };

function confidencePill(c: number): JSX.Element {
  const band = confidenceBand(c);
  const copy = KPI_CATALOG_IMPORT_COPY.review;
  const style: React.CSSProperties =
    band === "high"
      ? pillStyle
      : band === "medium"
        ? {
            ...pillStyle,
            background: "var(--color-warning-bg)",
            color: "var(--color-warning-text)",
          }
        : { ...pillStyle, background: "var(--color-bg)", color: "var(--color-text-3)" };
  const text =
    band === "high"
      ? copy.confidenceHigh
      : band === "medium"
        ? copy.confidenceMedium
        : copy.confidenceLow;
  return (
    <span style={style} title={`${Math.round(c * 100).toString()}%`}>
      {text}
    </span>
  );
}

export function CatalogImportReviewTable(props: CatalogImportReviewTableProps): JSX.Element {
  const { rows, issuesByRow, existingCategories, onPatch, disabled = false } = props;
  const copy = KPI_CATALOG_IMPORT_COPY.review;
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<CatalogImportFilter>("all");
  const [page, setPage] = useState(0);
  // K-U1 — sütunlar yalnız en az bir satır doluyken çizilir (eski dosyalarda tablo aynı kalır)
  const showDescription = rows.some((r) => r.description !== undefined && r.description !== "");
  const showRequirements = rows.some((r) => (r.requirements ?? []).length > 0);
  const colCount = 8 + (showDescription ? 1 : 0) + (showRequirements ? 1 : 0);

  const filtered = useMemo(
    () => filterRows(rows, filter, search, issuesByRow),
    [rows, filter, search, issuesByRow],
  );
  const pageCount = Math.max(1, Math.ceil(filtered.length / KPI_CATALOG_IMPORT_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const visible = paginate(filtered, safePage, KPI_CATALOG_IMPORT_PAGE_SIZE);
  const from = filtered.length === 0 ? 0 : safePage * KPI_CATALOG_IMPORT_PAGE_SIZE + 1;
  const to = Math.min(filtered.length, (safePage + 1) * KPI_CATALOG_IMPORT_PAGE_SIZE);

  function chip(on: boolean): React.CSSProperties {
    return {
      ...btnSmall,
      ...(on
        ? {
            background: "color-mix(in srgb, var(--color-primary) 10%, white)",
            color: "var(--color-primary)",
            borderColor: "var(--color-primary)",
            fontWeight: 600,
          }
        : {}),
    };
  }

  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <input
          type="search"
          aria-label={copy.searchPlaceholder}
          placeholder={copy.searchPlaceholder}
          style={{ ...inputStyle, width: 220, minHeight: 36 }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />
        <div role="group" aria-label="Süzgeç" className="flex gap-1 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              aria-pressed={filter === f.key}
              style={chip(filter === f.key)}
              onClick={() => {
                setFilter(f.key);
                setPage(0);
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div
        style={{
          overflowX: "auto",
          border: "1px solid var(--color-border)",
          borderRadius: 10,
          maxHeight: "52vh",
          overflowY: "auto",
        }}
      >
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 980 }}>
          <thead>
            <tr>
              <th style={thStyle}>{copy.columns.row}</th>
              <th style={thStyle}>{copy.columns.action}</th>
              <th style={thStyle}>{copy.columns.code}</th>
              <th style={thStyle}>{copy.columns.label}</th>
              <th style={thStyle}>{copy.columns.unit}</th>
              <th style={thStyle}>{copy.columns.category}</th>
              {showDescription ? <th style={thStyle}>{copy.descriptionColumn}</th> : null}
              {showRequirements ? <th style={thStyle}>{copy.requirementsColumn}</th> : null}
              <th style={thStyle}>{copy.columns.type}</th>
              <th style={thStyle}>{copy.columns.confidence}</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td style={{ ...tdStyle, color: "var(--color-text-3)" }} colSpan={colCount}>
                  {copy.empty}
                </td>
              </tr>
            ) : null}
            {visible.map((r) => {
              const issues = issuesByRow.get(r.rowId) ?? [];
              const isCategory = r.kind === "category";
              const isBreakdown = isBreakdownImportRow(r);
              const skipped = r.action === "skip";
              const cats = categoryOptions(rows, existingCategories, r.code);
              return (
                <tr
                  key={r.rowId}
                  style={{
                    background: isCategory ? bandRowBg : undefined,
                    opacity: skipped ? 0.55 : 1,
                  }}
                >
                  <td style={{ ...tdStyle, maxWidth: 260 }}>
                    <div style={{ paddingLeft: Math.min(r.depth, 4) * 12 }}>
                      <div style={{ fontWeight: isCategory ? 600 : 400 }}>
                        {r.rowLabel}
                        {isBreakdown ? (
                          <span
                            style={{
                              ...pillStyle,
                              marginLeft: 6,
                              background: "var(--color-bg)",
                              color: "var(--color-text-3)",
                            }}
                            title={copy.breakdownBadgeTitle}
                            data-testid="kpi-catalog-breakdown-badge"
                          >
                            {copy.breakdownBadge}
                          </span>
                        ) : null}
                      </div>
                      <div
                        style={{ ...monoStyle, color: "var(--color-text-3)", fontSize: "0.7rem" }}
                      >
                        {r.sheetName} · {r.rowId}
                        {r.sample ? ` · ${r.sample}` : ""}
                      </div>
                      {r.note ? (
                        <div style={{ fontSize: "0.72rem", color: "var(--color-text-2)" }}>
                          {r.note}
                        </div>
                      ) : null}
                      {issues.length > 0 ? (
                        <div style={{ fontSize: "0.72rem", color: "var(--color-error)" }}>
                          {issues.map(issueLabel).join(" · ")}
                        </div>
                      ) : null}
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <select
                      aria-label={`${copy.columns.action}: ${r.rowLabel}`}
                      style={{ ...cellInput, width: 150 }}
                      value={r.action}
                      disabled={disabled}
                      onChange={(e) =>
                        onPatch(r.rowId, { action: e.target.value as CatalogImportAction })
                      }
                    >
                      {ACTIONS.filter(
                        (a) => a !== "map_existing" || Boolean(r.existing) || r.action === a,
                      ).map((a) => (
                        <option key={a} value={a}>
                          {copy.actions[a]}
                        </option>
                      ))}
                    </select>
                    {r.action === "map_existing" && r.existing ? (
                      <label
                        className="flex items-center gap-1"
                        style={{ fontSize: "0.72rem", color: "var(--color-text-2)", marginTop: 4 }}
                      >
                        <input
                          type="checkbox"
                          checked={r.update === true}
                          disabled={disabled}
                          onChange={(e) => onPatch(r.rowId, { update: e.target.checked })}
                        />
                        {copy.updateLabel}
                      </label>
                    ) : null}
                  </td>
                  <td style={tdStyle}>
                    {r.action === "map_existing" ? (
                      <span style={monoStyle}>{r.code}</span>
                    ) : (
                      <input
                        aria-label={`${copy.columns.code}: ${r.rowLabel}`}
                        style={{ ...cellInput, ...monoStyle, width: 170 }}
                        value={r.code}
                        disabled={disabled || skipped}
                        onChange={(e) => onPatch(r.rowId, { code: e.target.value })}
                      />
                    )}
                  </td>
                  <td style={tdStyle}>
                    <input
                      aria-label={`${copy.columns.label}: ${r.rowLabel}`}
                      style={{ ...cellInput, width: 220 }}
                      value={r.label}
                      disabled={disabled || skipped}
                      onChange={(e) => onPatch(r.rowId, { label: e.target.value })}
                    />
                    {r.action === "map_existing" && r.existing ? (
                      <div
                        style={{ fontSize: "0.72rem", color: "var(--color-text-3)", marginTop: 2 }}
                      >
                        {copy.existingDiff(r.existing)}
                      </div>
                    ) : null}
                  </td>
                  <td style={tdStyle}>
                    {isCategory ? (
                      <span style={{ color: "var(--color-text-3)" }}>—</span>
                    ) : (
                      <input
                        aria-label={`${copy.columns.unit}: ${r.rowLabel}`}
                        style={{ ...cellInput, width: 90 }}
                        value={r.unit ?? ""}
                        disabled={disabled || skipped}
                        onChange={(e) => onPatch(r.rowId, { unit: e.target.value })}
                      />
                    )}
                    {!isCategory && (r.unitVariants ?? []).length > 0 ? (
                      <span
                        style={{ ...pillStyle, fontSize: "0.875rem", marginTop: 2 }}
                        title={(r.unitVariants ?? []).join(", ")}
                        data-testid="kpi-catalog-unit-variants"
                      >
                        {copy.unitAlternatives((r.unitVariants ?? []).length)}
                      </span>
                    ) : null}
                    {!isCategory && isUnitTemplate(r.unit) ? (
                      <span
                        style={{ fontSize: "0.875rem", color: "var(--color-text-3)" }}
                        title={copy.unitTemplateTitle}
                        data-testid="kpi-catalog-unit-template"
                      >
                        {" "}
                        {copy.unitTemplateHint}
                      </span>
                    ) : null}
                  </td>
                  <td style={tdStyle}>
                    {isBreakdown ? (
                      <div data-testid="kpi-catalog-breakdown-path">
                        <div style={{ overflowWrap: "anywhere" }}>
                          {breakdownParentPath(r, rows) || "—"}
                        </div>
                        <div style={{ fontSize: "0.72rem", color: "var(--color-text-3)" }}>
                          {copy.breakdownParentHint}
                        </div>
                      </div>
                    ) : (
                      <select
                        aria-label={`${copy.columns.category}: ${r.rowLabel}`}
                        style={{ ...cellInput, width: 180 }}
                        value={r.categoryCode ?? ""}
                        disabled={disabled || skipped}
                        onChange={(e) => onPatch(r.rowId, { categoryCode: e.target.value })}
                      >
                        <option value="">{copy.noCategory}</option>
                        {cats.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.fromFile ? `＋ ${c.label}` : c.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  {showDescription ? (
                    <td
                      style={{ ...tdStyle, maxWidth: 240, fontSize: "0.78rem" }}
                      title={r.description ?? ""}
                      data-testid="kpi-catalog-description"
                    >
                      {r.description ?? ""}
                    </td>
                  ) : null}
                  {showRequirements ? (
                    <td
                      style={{ ...tdStyle, fontSize: "0.78rem" }}
                      data-testid="kpi-catalog-requirements"
                    >
                      {(r.requirements ?? []).map((q) => `${q.standard} ${q.code}`).join("; ")}
                    </td>
                  ) : null}
                  <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                    {copy.kinds[r.kind]}
                    {!isCategory ? (
                      <label
                        className="flex items-center gap-1"
                        style={{ fontSize: "0.72rem", color: "var(--color-text-2)", marginTop: 4 }}
                        title={r.formula ?? ""}
                      >
                        <input
                          type="checkbox"
                          checked={r.isDerived}
                          disabled={disabled || skipped || !r.formula}
                          onChange={(e) => onPatch(r.rowId, { isDerived: e.target.checked })}
                        />
                        {copy.derivedPill}
                        {r.formula ? (
                          <span style={{ ...monoStyle, fontSize: "0.7rem" }}>{r.formula}</span>
                        ) : null}
                      </label>
                    ) : null}
                  </td>
                  <td style={tdStyle}>{confidencePill(r.confidence)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-2 mt-2" style={{ justifyContent: "flex-end" }}>
        <span style={{ fontSize: "0.78rem", color: "var(--color-text-3)" }}>
          {copy.pageInfo(from, to, filtered.length)}
        </span>
        <button
          type="button"
          style={btnGhost}
          disabled={safePage === 0}
          onClick={() => setPage(safePage - 1)}
        >
          {copy.prev}
        </button>
        <button
          type="button"
          style={btnGhost}
          disabled={safePage >= pageCount - 1}
          onClick={() => setPage(safePage + 1)}
        >
          {copy.next}
        </button>
      </div>
    </div>
  );
}
